import { useEffect, useMemo, useRef } from 'react';

import { useActiveRoutePlan } from '@/hooks/use-active-route-plan';
import { useRouteGeocoder, useRoutePlanStore } from '@/hooks/use-scoped-managers';
import { RouteMapPinBuilder, type RouteMapModel } from '@/lib/route-plan/RouteMapPinBuilder';

/**
 * useRouteMapModel — useActiveRoutePlan plus the derived map model, with a
 * one-shot quiet geocode retry that backfills coordinates onto saved plans
 * that are missing them (offline saves, plans saved before this feature).
 */
export function useRouteMapModel() {
  const active = useActiveRoutePlan();
  const routeGeocoder = useRouteGeocoder();
  const routePlanStore = useRoutePlanStore();
  const hasRetriedRef = useRef(false);

  const { plan, currentLegIndex, refresh } = active;

  useEffect(() => {
    if (!plan || !routeGeocoder || !routePlanStore || hasRetriedRef.current) return;
    const needsGeocoding = plan.stops.some(
      (stop) => stop.location?.trim() && stop.latitude === undefined,
    );
    if (!needsGeocoding) return;
    hasRetriedRef.current = true;

    routeGeocoder
      .geocodeStops(plan.stops)
      .then(async (stops) => {
        const changed = stops.some((stop, index) => stop !== plan.stops[index]);
        if (!changed) return;
        await routePlanStore.updatePlan(plan.date, (current) => ({ ...current, stops }));
        await refresh();
      })
      .catch((error) => {
        console.error('[useRouteMapModel] Geocode retry failed:', error);
      });
  }, [plan, routeGeocoder, routePlanStore, refresh]);

  const model: RouteMapModel | null = useMemo(() => {
    if (!plan) return null;
    return RouteMapPinBuilder.build({
      stops: plan.stops,
      legs: plan.legs,
      completedLegIds: plan.completedLegIds,
      currentLegIndex,
    });
  }, [plan, currentLegIndex]);

  return { ...active, model };
}
